import { UserApi } from '@intelingu/core/api/UserApi';
import { action, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Component } from 'react';
import { SubscriptionsApi } from '@intelingu/core/api/SubscriptionsApi';
import { COMMON_NS } from '@intelingu/core/constants/base.constants';
import { UserSubscription } from '@intelingu/core/interfaces/models/UserSubscriptionModel';
import { lazyInject } from '@intelingu/core/IoC/container';
import { UserStore } from '@intelingu/core/stores/UserStore';
import Logger from '@intelingu/core/utils/Logger';
import withTranslation from '@intelingu/core/utils/withTranslation';
import { Redirect } from 'react-router';
import { SubscriptionItem } from '../partials/SubscriptionItem';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import {CourseStore} from "@intelingu/core/stores/CourseStore";

export interface AccountTabProps {
    t?;
}

@withTranslation(COMMON_NS)
@observer export class AccountTab extends Component<AccountTabProps, any> {

    @lazyInject(UserStore) userStore: UserStore;
    @lazyInject(CourseStore) courseStore: CourseStore;
    @lazyInject(SubscriptionsApi) subscriptionsApi: SubscriptionsApi;
    @lazyInject(UserApi) userApi: UserApi;

    @observable isSubscriptionsLoaded = false;
    @observable subscriptions: UserSubscription[] = [];
    @observable isModalVisible = false;
    @observable isAccountRemoved = false;

    get isTrial() {
        return !this.accessibleCourses.length;
    }

    get accessibleCourses() {
        return this.courseStore.coursesList.filter(course => course.is_available);
    }

    get accessibleOtherCourses(): Array<{ name: string }> {
        if (this.isTrial) {
            return [{ name: 'Trial' }]
        }

        const subscribedIds = this.subscriptions
            .filter(item => !item.is_trial)
            .map(item => item.course_id);

        return this.courseStore.coursesList
            .filter(course => course.is_available && !subscribedIds.includes(course.id))
            .map(item => ({ name: item.name }));
    }

    isVisible(subscription: UserSubscription) {
        if (!subscription.is_trial) {
            return true;
        }

        return !this.subscriptions.filter((item) => !item.is_trial && item.course_id === subscription.course_id).length;
    }

    async componentDidMount() {
        try {
            const response = await this.subscriptionsApi.getSubscriptions();
            this.subscriptions = response.data;
            this.isSubscriptionsLoaded = true;
        } catch (e) {
            Logger.error(e);
        }
    }

    @action.bound toggleRemoveModal() {
        this.isModalVisible = !this.isModalVisible;
    }

    @action.bound removeAccount() {
        this.userApi.remove();
        this.isModalVisible = false;
        this.isAccountRemoved = true;
    }

    _renderDeleteAccountLink() {
        if (!this.isSubscriptionsLoaded || this.subscriptions.filter(s => (!s.is_trial && !s.is_expired)).length) {
            return null;
        }

        return <a href={''} className={'red-link'} onClick={e => {
            e.preventDefault();
            this.isModalVisible = true;
        }}>Mein Intelingu Konto permanent löschen</a>;
    }

    render() {
        const { t } = this.props;

        if (this.isAccountRemoved) {
            return <Redirect to={`/session/removed`} />
        }

        return <>
            <div className={'tab-title'}>
                <h3>{ t('profileAccountInfoTitle') }</h3>
                <p>{ t('profileAccountInfoSubtitle', { email: this.userStore.user.email }) }</p>
            </div>

            {/*<h4 className={'mb-3'}>Payment method</h4>
            <div className="list-group list-group-flush payment-details">
                <div className="list-group-item">
                    <div className={'row'}>
                        <div className={'col col-12 col-md-6'}><span className={'th'}>Payment method</span></div>
                        <div className={'col col-12 col-md-6'}>SEPA</div>
                    </div>
                </div>
                <div className="list-group-item">
                    <div className={'row'}>
                        <div className={'col col-12 col-md-6'}><span className={'th'}>IBAN</span></div>
                        <div className={'col col-12 col-md-6'}>****3341</div>
                    </div>
                </div>
            </div>*/}

            {/*<div className={'alert alert-info'}>
                Payment method is not specified yet.
            </div>*/}

            <h4 className={'mt-5 mb-3'}>{ t('subscriptionsTitle') }</h4>
            <div className="subscriptions-list">
                <div className="item thead d-md-block">
                    <div className={'row'}>
                        <div className={'col-xs-12 col-sm'}><span className={'th'}>{ t('subscriptionsType') }</span></div>
                        <div className={'col-xs-12 col-sm'}><span className={'th'}>{ t('subscriptionsDetails') }</span></div>
                        <div className={'col-xs-12 col-sm'}><span className={'th'}>{ t('subscriptionsManage') }</span></div>
                    </div>
                </div>

                { this.accessibleOtherCourses.map((item, index) => (
                    <div className="item mb-4" key={index}>
                        <div className={'row'}>
                            <div className={'col-md d-md-none mb-1'}><span className={'th'}>{item.name}</span></div>
                            <div className={'col-xs-12 col-sm'}><span>{item.name}</span></div>

                            <div className={'col-md d-md-none mb-1 mt-3'}><span className={'th'}>{ t('subscriptionsDetails') }</span></div>
                            <div className={'col-xs-12 col-sm'}>-</div>
                            <div className={'col-xs-12 col-sm'}>-</div>
                        </div>
                    </div>
                )) }

                { this.subscriptions
                    .filter(subscription => !subscription.is_trial)
                    .map(item => this.isVisible(item) ? <SubscriptionItem subscription={item} key={item.id} /> : null) }
            </div>

            { this._renderDeleteAccountLink() }

            <Modal isOpen={this.isModalVisible} toggle={this.toggleRemoveModal}>
                <ModalHeader toggle={this.toggleRemoveModal}>{ t('removeModalTitle') }</ModalHeader>
                <ModalBody>
                    { t('removeModalContent') }
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={this.removeAccount}>{ t('removeModalAgree') }</Button>{' '}
                    <Button color="secondary" onClick={this.toggleRemoveModal}>{ t('removeModalCancel') }</Button>
                </ModalFooter>
            </Modal>
        </>
    }

}
